/**
 * Baremetal Reservations Page
 * Displays user's GPU reservations from the marketplace service
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CpuChipIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CommandLineIcon,
  DocumentDuplicateIcon,
  CreditCardIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../../components/layout/Header';
import { marketplaceApi } from '../../services/marketplaceApi';
import { ReservationResponse, ReservationFilter, SSHAccessInfo, PaymentStatus, ProvisioningStatus } from '../../types/marketplace';

const Reservations: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedReservation, setSelectedReservation] = useState<ReservationResponse | null>(null);
  const [showSSHModal, setShowSSHModal] = useState(false);
  const [sshAccess, setSSHAccess] = useState<SSHAccessInfo | null>(null);

  const filters: ReservationFilter = {
    status: statusFilter as ReservationFilter['status'] || undefined,
    page,
    pageSize: 10,
  };

  // Fetch reservations using the new API with payment status
  const { data: reservationsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-reservations', filters],
    queryFn: () => marketplaceApi.listMyReservations(filters),
  });

  // Cancel reservation mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => marketplaceApi.cancelReservation(id),
    onSuccess: () => {
      toast.success('Reservation cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['marketplace-reservations'] });
    },
    onError: () => {
      toast.error('Failed to cancel reservation');
    },
  });

  // Get SSH access mutation
  const getSSHMutation = useMutation({
    mutationFn: (id: string) => marketplaceApi.getSSHAccess(id),
    onSuccess: (data) => {
      setSSHAccess(data);
      setShowSSHModal(true);
    },
    onError: () => {
      toast.error('Failed to get SSH access details');
    },
  });

  const reservations = reservationsData?.data || [];
  const pagination = reservationsData?.pagination;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            <ClockIcon className="w-3 h-3" />
            Pending
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            <CheckCircleIcon className="w-3 h-3" />
            Active
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
            <CheckCircleIcon className="w-3 h-3" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
            <XCircleIcon className="w-3 h-3" />
            Cancelled
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            <ExclamationTriangleIcon className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
            {status}
          </span>
        );
    }
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            <CreditCardIcon className="w-3 h-3" />
            Pending
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            <CheckCircleIcon className="w-3 h-3" />
            Paid
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            <XCircleIcon className="w-3 h-3" />
            Failed
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
            <CreditCardIcon className="w-3 h-3" />
            Refunded
          </span>
        );
      default:
        return null;
    }
  };

  const getProvisioningStatusBadge = (status: ProvisioningStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            <ClockIcon className="w-3 h-3" />
            Pending
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
            <ServerStackIcon className="w-3 h-3 animate-pulse" />
            Provisioning
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            <CheckCircleIcon className="w-3 h-3" />
            Ready
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            <ExclamationTriangleIcon className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return currency === 'INR' ? `₹${price.toFixed(2)}` : `$${price.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div>
      <Header
        title="My Reservations"
        subtitle="View and manage your baremetal GPU reservations"
      />

      <div className="p-6 space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-300">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-800 text-white"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Reservations Table */}
        {isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading reservations...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="text-center">
              <p className="text-red-400 mb-4">Failed to load reservations</p>
              <button onClick={() => refetch()} className="btn-primary">
                Retry
              </button>
            </div>
          </div>
        ) : reservations.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="text-center">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-white">No reservations</h3>
              <p className="mt-1 text-sm text-gray-400">
                {statusFilter
                  ? `No ${statusFilter} reservations found.`
                  : 'You have not made any reservations yet.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Reservation
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      GPU
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Configuration
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Instance
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {reservations.map((reservation: ReservationResponse) => (
                    <tr key={reservation.id} className="hover:bg-gray-800">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-white">{reservation.code}</p>
                          <p className="text-xs text-gray-400">
                            {formatDate(reservation.createdAt)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <CpuChipIcon className="w-5 h-5 text-gray-600" />
                          <div>
                            <p className="font-medium text-white">{reservation.gpu.name}</p>
                            <p className="text-xs text-gray-400">{reservation.gpu.vramGB} GB VRAM</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {reservation.configuration.gpuCount}x GPU |{' '}
                        {reservation.configuration.cpuCores} vCPU |{' '}
                        {reservation.configuration.ramGB} GB RAM
                      </td>
                      <td className="px-6 py-4">
                        {getPaymentStatusBadge(reservation.payment.status)}
                        {reservation.payment.paidAt && (
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(reservation.payment.paidAt)}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {reservation.payment.status === 'paid' ? (
                          <>
                            {getProvisioningStatusBadge(reservation.provisioning.status)}
                            {reservation.provisioning.status === 'completed' && reservation.ssh && (
                              <p className="text-xs text-green-400 mt-1">SSH Ready</p>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">Awaiting payment</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-white">
                          {formatPrice(reservation.payment.amount || reservation.pricing.estimatedCost, 'INR')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {reservation.timing.durationHours} hours
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Show SSH button only when provisioning is completed */}
                          {reservation.provisioning.status === 'completed' && reservation.ssh && (
                            <button
                              onClick={() => {
                                setSelectedReservation(reservation);
                                // Use SSH from reservation directly
                                setSSHAccess(reservation.ssh!);
                                setShowSSHModal(true);
                              }}
                              className="text-sm text-primary-500 hover:text-primary-400 font-medium flex items-center gap-1"
                            >
                              <CommandLineIcon className="w-4 h-4" />
                              SSH
                            </button>
                          )}
                          {/* Cancel button only for pending payment */}
                          {reservation.payment.status === 'pending' && (
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    'Are you sure you want to cancel this reservation?'
                                  )
                                ) {
                                  cancelMutation.mutate(reservation.id);
                                }
                              }}
                              disabled={cancelMutation.isPending}
                              className="text-sm text-red-400 hover:text-red-300 font-medium disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-300">
                  Showing page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pagination.pages}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* SSH Access Modal */}
      {showSSHModal && sshAccess && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              SSH Access - {selectedReservation.code}
            </h3>

            {sshAccess.status === 'provisioning' ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-sm text-gray-400">
                  Server is being provisioned. SSH access will be available shortly.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    SSH Command
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-700 rounded-lg text-sm font-mono text-gray-300 overflow-x-auto">
                      ssh {sshAccess.username}@{sshAccess.host} -p {sshAccess.port}
                    </code>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `ssh ${sshAccess.username}@${sshAccess.host} -p ${sshAccess.port}`
                        )
                      }
                      className="p-2 text-gray-400 hover:text-gray-300"
                    >
                      <DocumentDuplicateIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Host</label>
                    <p className="text-sm text-white">{sshAccess.host}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Port</label>
                    <p className="text-sm text-white">{sshAccess.port}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                    <p className="text-sm text-white">{sshAccess.username}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                      Ready
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowSSHModal(false);
                  setSSHAccess(null);
                  setSelectedReservation(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reservations;
